import React, { useEffect } from 'react';
import {
  Center,
  VStack,
  Flex,
  Text,
  SimpleGrid,
  useBreakpointValue,
} from '@wegroup/design-system';
import ErrorPage from '../../../../common/src/components/ErrorPage';
import LoadingScreen from '../../../../common/src/components/LoadingScreen';
import { useCompleteConversation } from '../../../../conversations/src/features/detail/shared/api/completeConversation';
import { useConversation } from '../../../../conversations/src/features/detail/shared/api/getConversationById';
import AdvisoryAppShell from '../../../../flows/src/components/ui/advisory-flow/components/app-shell/AdvisoryAppShell';
import useSessionId from '../../../../flows/src/hooks/useSessionId';
import { useTranslation } from 'react-i18next';
import SmallToolbarWithBack from '../../../../risk-analysis/src/components/recommendations/SmallToolbarWithBack';
import TechniekNederlandFinalizeBody from './TechniekNederlandFinalizeBody';
import { ConversationStatus } from '../../../../conversations/src/features/overview/types/OverviewTypes';
import { useCreateAdvisorReport } from '../../../../advisory-report/src/api/createAdvisoryReport';
import useQueryParams from '../../../../common/src/hooks/useQueryParams';
import useSetQueryParam from '../../../../common/src/hooks/query-params/useAddQueryParam';
import NoReportBanner from './NoReportBanner';
import useAuth from '../../../../app/src/features/authentication/hooks/useAuth';

const TechniekNederlandFinalize: React.FC = () => {
  const { t } = useTranslation();
  const { campaign } = useAuth();
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  const sessionId = useSessionId();
  const queryParams = useQueryParams();
  const setQueryParam = useSetQueryParam();
  const {
    mutateAsync: completeConversation,
    isLoading,
    isError,
  } = useCompleteConversation(sessionId, false);
  const {
    mutateAsync: createAdvisoryReport,
    data,
    isLoading: isCreatingReport,
  } = useCreateAdvisorReport();

  const { data: conversation, isLoading: isLoadingConversation } =
    useConversation(sessionId);

  const initial = async () => {
    const completeData = await completeConversation(undefined);
    if (
      conversation &&
      conversation.prospect?.id &&
      completeData?.recommendation_id
    ) {
      const data = await createAdvisoryReport({
        partyId: conversation.prospect.id,
        recommendationId: completeData.recommendation_id,
        conversationId: conversation.id,
        lang: conversation.language,
        assigned_to: campaign?.assigned_to || undefined,
      });
      if (data.id) {
        queryParams.set('reportId', data.id);
        setQueryParam({ reportId: data.id });
      }
    }
  };

  useEffect(() => {
    if (
      conversation?.prospect?.id &&
      conversation.status !== ConversationStatus.COMPLETED
    ) {
      initial();
    }
  }, [conversation?.prospect?.id]);

  if (isError)
    return (
      <AdvisoryAppShell>
        <ErrorPage />
      </AdvisoryAppShell>
    );

  if (
    isLoading ||
    isLoadingConversation ||
    isCreatingReport ||
    (!(conversation?.status === ConversationStatus.COMPLETED) &&
      !data &&
      !queryParams.get('reportId'))
  )
    return <LoadingScreen />;

  return (
    <>
      <Flex direction={{ base: 'column', tablet: 'row' }} h="100%" w="100%">
        {!isMobile && <SmallToolbarWithBack tooltipText={t('GO-BACK')} />}
        <Center h="100%" flexGrow={1} p="6" bg="#FBFBFB" overflowY="auto">
          <VStack spacing="8" align="stretch" maxW="4xl">
            <Text textAlign="center" textStyle="h3Headline">
              {t('COMPANY-SCAN_RECOMMENDATIONS_FINISHED_TITLE')}
            </Text>
            <NoReportBanner
              isShown={!data?.id && !queryParams.get('reportId')}
            />
            <SimpleGrid
              columns={{
                base: 1,
                // laptop: 2,
              }}
              maxW="sm"
              gap="4"
              alignSelf="center"
            >
              <TechniekNederlandFinalizeBody
                reportId={data?.id || queryParams.get('reportId') || undefined}
                partyId={conversation?.prospect?.id || ''}
                partyEmail={data?.party.email}
              />
            </SimpleGrid>
          </VStack>
        </Center>
      </Flex>
    </>
  );
};

export default TechniekNederlandFinalize;
